import { memo, useCallback, useMemo, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import { Alert, Box, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { MdOutlineAutoAwesome, MdOutlineErrorOutline, MdOutlineShield } from "react-icons/md";
import { QuestionList } from "../question-list";
import { ToolCall } from "../tool-call";
import { SpeechConfig, V2Delta, V2Source } from "../../api";
import classes from "./Response.module.css";
import { ResponseControls } from "./ResponseControls";
import { DevelopmentSupportData, ThoughtProcessData } from "../types";
import { CitationList } from "../citation-list/CitationList";
import { ChatErrorKind } from "../../utils/chatError";

export interface ResponseProps {
    index?: number;
    isLoading: boolean;
    hasError?: boolean;
    errorKind?: ChatErrorKind;
    errorMessage?: string;
    onSourceClick: (source: V2Source, anchor: string) => void;
    onFollowupQuestionClick: (question: string) => void;
    speechConfig: SpeechConfig;
    showFeedback: boolean;
    response?: V2Delta;
}

// Visual treatment per error kind. Content-filter uses a shield icon and a
// warning color to distinguish a policy block from a transient failure.
const ERROR_PRESENTATION: Record<ChatErrorKind, { color: string; icon: ReactNode }> = {
    generic: { color: "red", icon: <MdOutlineErrorOutline size={20} /> },
    rate_limit: { color: "red", icon: <MdOutlineErrorOutline size={20} /> },
    content_filter: { color: "yellow", icon: <MdOutlineShield size={20} /> }
};

// Plugin arrays and static renderers are hoisted so their identity is stable across
// renders; inline definitions would make ReactMarkdown remount the rendered elements
// on every streaming tick.
const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeRaw];

const MarkdownH2 = ({ children, ...props }: any) => (
    <h2 className={classes.markdownH2} {...props}>
        {children}
    </h2>
);

const MarkdownH3 = ({ children, ...props }: any) => (
    <h3 className={classes.markdownH3} {...props}>
        {children}
    </h3>
);

export const ResponseComponent = memo(
    ({ isLoading, response, speechConfig, onSourceClick, onFollowupQuestionClick, showFeedback, index, hasError, errorKind, errorMessage }: ResponseProps) => {
        const [isFocused, setIsFocused] = useState(false);
        const { t } = useTranslation();
        const sanitizedAnswerHtml = useMemo(() => (response?.response ? DOMPurify.sanitize(response.response) : undefined), [response?.response]);
        const plainText = useMemo(
            () => sanitizedAnswerHtml && sanitizedAnswerHtml.replace(/<sup>\d+<\/sup>/g, "").replace(/\[[^\]]+\]\([^)]+\)/g, ""),
            [sanitizedAnswerHtml]
        );
        const developmentSupportData: DevelopmentSupportData | undefined = useMemo(
            () =>
                response
                    ? {
                          sources: response.sources ?? [],
                          thoughtProcessData: (response.thoughts ?? []) as ThoughtProcessData[],
                          supportingContent: { data: [] }
                      }
                    : undefined,
            [response]
        );

        const onSourceClickHandler = useCallback(
            (source: V2Source, anchor: string) => {
                onSourceClick(source, anchor);
                setIsFocused(true);
                setTimeout(() => {
                    setIsFocused(false);
                }, 2000);
            },
            [onSourceClick]
        );

        const sources = response?.sources;
        const markdownComponents = useMemo(
            () => ({
                h2: MarkdownH2,
                h3: MarkdownH3,
                a: ({ href, children }: any) => {
                    const text = Array.isArray(children) ? children[0] : children;
                    const n = typeof text === "string" ? parseInt(text, 10) : NaN;
                    if (!isNaN(n) && n >= 1 && sources) {
                        const anchor = href?.includes("#") ? href.split("#")[1] || "" : "";
                        const source = sources[n - 1];
                        if (source) {
                            return (
                                <sup style={{ cursor: "pointer" }} onClick={() => onSourceClickHandler(source, anchor)}>
                                    {text}
                                </sup>
                            );
                        }
                    }
                    return <a href={href}>{children}</a>;
                }
            }),
            [sources, onSourceClickHandler]
        );

        return (
            <Stack align="flex-start" justify="center" gap="xs">
                <Group justify="flex-end" gap="xs">
                    <MdOutlineAutoAwesome size={16} className={classes.icon} />
                    <Text size="sm" fw={500}>
                        ARTI
                    </Text>
                </Group>

                <Paper className={`${classes.responseContainer} ${isFocused ? classes.focusedResponseContainer : ""}`}>
                    {!hasError && response?.toolCalls && response.toolCalls.length > 0 && (
                        <Stack gap="xs">
                            {response.toolCalls.map(tc => (
                                <ToolCall key={tc.id} toolCall={tc} />
                            ))}
                        </Stack>
                    )}

                    {hasError && (
                        <Alert variant="light" color={ERROR_PRESENTATION[errorKind ?? "generic"].color} icon={ERROR_PRESENTATION[errorKind ?? "generic"].icon}>
                            {errorMessage ?? t("error")}
                        </Alert>
                    )}

                    {!hasError && (isLoading || !!response?.toolCalls?.length) && !sanitizedAnswerHtml && <Loader size={16} type="dots" />}

                    {!isLoading && !hasError && (
                        <Stack>
                            <Box>
                                <ReactMarkdown
                                    children={sanitizedAnswerHtml}
                                    rehypePlugins={REHYPE_PLUGINS}
                                    remarkPlugins={REMARK_PLUGINS}
                                    components={markdownComponents}
                                />
                            </Box>
                            <ResponseControls
                                conversationId={response?.id ?? undefined}
                                response={plainText}
                                speechConfig={speechConfig}
                                index={index}
                                showFeedback={showFeedback}
                                developmentSupportData={developmentSupportData}
                            />
                            <CitationList sources={response?.sources || []} onSourceClick={onSourceClickHandler} response={response} />
                            <QuestionList questions={response?.followUpQuestions || []} onQuestionClick={onFollowupQuestionClick} />
                        </Stack>
                    )}
                </Paper>
            </Stack>
        );
    }
);
